// XML/JSON content-type detection and normalization for the compare/diff and viewers.
// Normalizing both sides before diffing makes the diff structural rather than cosmetic:
// JSON keys are sorted; XML attributes are sorted and indentation is consistent.

export type PayloadFormat = 'xml' | 'json' | 'unknown';

export const detectFormat = (payload?: string | null): PayloadFormat => {
  if (!payload) return 'unknown';
  const trimmed = payload.trimStart();
  if (trimmed.length === 0) return 'unknown';
  const first = trimmed[0];
  if (first === '{' || first === '[') return 'json';
  if (first === '<') return 'xml';
  return 'unknown';
};

export const contentTypeLabel = (payload?: string | null): string => {
  switch (detectFormat(payload)) {
    case 'xml':
      return 'XML';
    case 'json':
      return 'JSON';
    default:
      return 'TEXT';
  }
};

const sortKeys = (value: any): any => {
  if (Array.isArray(value)) return value.map(sortKeys);
  if (value && typeof value === 'object') {
    return Object.keys(value)
      .sort()
      .reduce((acc: Record<string, any>, key) => {
        acc[key] = sortKeys(value[key]);
        return acc;
      }, {});
  }
  return value;
};

export const normalizeJson = (json: string): string => {
  try {
    return JSON.stringify(sortKeys(JSON.parse(json)), null, 2);
  } catch {
    return json.trim();
  }
};

const escapeXmlText = (text: string): string =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const escapeXmlAttr = (text: string): string => escapeXmlText(text).replace(/"/g, '&quot;');

const serializeElement = (el: Element, depth: number): string => {
  const indent = '  '.repeat(depth);
  const attrs = Array.from(el.attributes)
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((a) => ` ${a.name}="${escapeXmlAttr(a.value)}"`)
    .join('');

  const childElements = Array.from(el.childNodes).filter((n) => n.nodeType === 1) as Element[];
  const text = Array.from(el.childNodes)
    .filter((n) => n.nodeType === 3)
    .map((n) => (n.nodeValue ?? '').trim())
    .join('');

  const tag = el.tagName;

  if (childElements.length === 0) {
    return text
      ? `${indent}<${tag}${attrs}>${escapeXmlText(text)}</${tag}>`
      : `${indent}<${tag}${attrs}/>`;
  }

  const inner = childElements.map((c) => serializeElement(c, depth + 1)).join('\n');
  return `${indent}<${tag}${attrs}>\n${inner}\n${indent}</${tag}>`;
};

export const normalizeXml = (xml: string): string => {
  try {
    const doc = new DOMParser().parseFromString(xml, 'application/xml');
    if (doc.getElementsByTagName('parsererror').length > 0 || !doc.documentElement) {
      return xml.trim();
    }
    return serializeElement(doc.documentElement, 0);
  } catch {
    return xml.trim();
  }
};

export const normalize = (payload?: string | null): string => {
  if (!payload) return '';
  switch (detectFormat(payload)) {
    case 'json':
      return normalizeJson(payload);
    case 'xml':
      return normalizeXml(payload);
    default:
      return payload.trim();
  }
};
